import { useState } from "react";
import { toast } from "sonner";
import { Users } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";

const ROLES = ["buyer", "seller", "service_provider", "supplier"];
const ORIGINS = ["real", "test", "demo"];
const EMPTY = { legal_name: "", industry: "", business_email: "", country: "", products: "", website: "", opportunity: "", role: "buyer", data_origin: "real" };

export const AddLeadDialog = ({ open, onOpenChange, onCreated }) => {
  const [f, setF] = useState(EMPTY);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e?.target ? e.target.value : e }));

  const submit = async () => {
    if (submitting) return;
    if (!f.legal_name.trim()) { setError("Business legal name is required."); return; }
    if (f.business_email && !/^\S+@\S+\.\S+$/.test(f.business_email.trim())) { setError("Enter a valid business email or leave it blank."); return; }
    setError(""); setSubmitting(true);
    try {
      await api.post("/admin/leads", { ...f, legal_name: f.legal_name.trim(), business_email: f.business_email.trim().toLowerCase(), source_type: "manual_entry" });
      toast.success("Lead added.");
      setF(EMPTY); onOpenChange(false); onCreated?.();
    } catch (e) { setError(formatApiError(e?.response?.data?.detail) || "Could not add lead."); }
    finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!submitting) { onOpenChange(o); if (!o) { setF(EMPTY); setError(""); } } }}>
      <DialogContent className="max-w-lg" data-testid="add-lead-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Users size={18} className="text-[#047857]" /> Add a business lead</DialogTitle>
          <DialogDescription>Enter only business information you are legally permitted to use. AI qualification is advisory; every invitation is human-approved.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3 py-1 max-h-[60vh] overflow-y-auto pr-1">
          <div className="grid gap-1.5">
            <Label htmlFor="al-name">Business legal name <span className="text-red-500">*</span></Label>
            <Input id="al-name" data-testid="add-lead-name" value={f.legal_name} onChange={set("legal_name")} placeholder="Acme Trading Ltd" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5"><Label htmlFor="al-ind">Industry</Label><Input id="al-ind" data-testid="add-lead-industry" value={f.industry} onChange={set("industry")} placeholder="Agriculture" /></div>
            <div className="grid gap-1.5"><Label htmlFor="al-country">Country</Label><Input id="al-country" data-testid="add-lead-country" value={f.country} onChange={set("country")} placeholder="Ghana" /></div>
          </div>
          <div className="grid gap-1.5"><Label htmlFor="al-email">Business email</Label><Input id="al-email" data-testid="add-lead-email" value={f.business_email} onChange={set("business_email")} placeholder="trade@acme.example" /></div>
          <div className="grid gap-1.5"><Label htmlFor="al-web">Website</Label><Input id="al-web" data-testid="add-lead-website" value={f.website} onChange={set("website")} placeholder="https://acme.example" /></div>
          <div className="grid gap-1.5"><Label htmlFor="al-prod">Products / services</Label><Input id="al-prod" data-testid="add-lead-products" value={f.products} onChange={set("products")} placeholder="Cocoa beans, cocoa butter" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5"><Label>Supply-chain role</Label>
              <Select value={f.role} onValueChange={set("role")}>
                <SelectTrigger data-testid="add-lead-role"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r}>{r.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid gap-1.5"><Label>Data origin</Label>
              <Select value={f.data_origin} onValueChange={set("data_origin")}>
                <SelectTrigger data-testid="add-lead-origin"><SelectValue /></SelectTrigger>
                <SelectContent>{ORIGINS.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-1.5"><Label htmlFor="al-opp">Stated opportunity</Label><Textarea id="al-opp" data-testid="add-lead-opportunity" value={f.opportunity} onChange={set("opportunity")} rows={2} placeholder="Bulk cocoa supply to EU buyers" /></div>
          {f.data_origin !== "real" && <p className="text-xs text-amber-600">Non-real data is excluded from real beta conversion metrics.</p>}
          {error && <p className="text-sm text-red-600" data-testid="add-lead-error">{error}</p>}
        </div>
        <DialogFooter>
          <button data-testid="add-lead-cancel" disabled={submitting} onClick={() => onOpenChange(false)} className="h-9 px-3 rounded-md border border-slate-200 text-sm disabled:opacity-40">Cancel</button>
          <button data-testid="add-lead-submit" disabled={submitting} onClick={submit} className="h-9 px-4 rounded-md bg-[#047857] text-white text-sm disabled:opacity-50">{submitting ? "Adding…" : "Add lead"}</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
